import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import {
  getColumnsWidthStyles,
  getSharedFormStyles,
} from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { AutocompleteCDS, CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { array, mixed, object } from 'yup';

import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';

import { showMessageAction } from 'store/global/actions';
import { updateLoanDenialReasonsAction } from 'store/loanApplications/actions';
import {
  selectFormLockedFieldsMap,
  selectFormLockedPage,
  selectLoanApplicationBorrowerOptions,
  selectLoanDenialReasons,
} from 'store/loanApplications/selectors';
import {
  selectDenialReasonsData,
  selectDenialReasonsOptions,
  selectHMDACodeMap,
} from 'store/system/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

import { COLUMNS_COUNT } from './LoanDenial.constants';
import { getLoanDenialStyles } from './LoanDenial.styles';

const schema = array().of(
  object().shape({
    selectedBorrowers: mixed().test({
      message: 'At least one borrower must be selected',
      test: borrowerValues => !borrowerValues.length || borrowerValues.some(value => value),
    }),
  }),
);

const LoanDenialForm = ({ isLoading, setIsLoading, onExitEditMode }) => {
  const dispatch = useDispatch();

  const loanId = useLoanId();

  const theme = useTheme();

  const formClasses = useMemo(() => getSharedFormStyles(theme), [theme]);
  const classes = getLoanDenialStyles(theme);

  const borrowerOptions = useSelector(selectLoanApplicationBorrowerOptions);
  const denialReasonOptions = useSelector(selectDenialReasonsOptions);
  const allDenialReasonsData = useSelector(selectDenialReasonsData);
  const loanDenialReasons = useSelector(selectLoanDenialReasons);
  const HMDACodeMap = useSelector(selectHMDACodeMap);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.DENIAL_REASONS));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.DENIAL_REASONS));

  const isDisabledByLock = isLoading || isPageLocked;

  const [isAddingMode, setIsAddingMode] = useState(false);

  const onShowAddField = useCallback(() => setIsAddingMode(true), []);

  const columnsCount = borrowerOptions.length + COLUMNS_COUNT;
  const columnsStyles = useMemo(
    () => getColumnsWidthStyles(theme, { columns: columnsCount }),
    [theme, columnsCount],
  );

  const formContainerSx = useMemo(
    () => [formClasses.root, formClasses.container],
    [formClasses.root, formClasses.container],
  );
  const topHeaderSx = useMemo(
    () => [columnsStyles.wrapper, classes.topHeader],
    [columnsStyles.wrapper, classes.topHeader],
  );
  const sideHeaderSx = useMemo(
    () => [columnsStyles.wrapper, classes.sideHeader],
    [columnsStyles.wrapper, classes.sideHeader],
  );
  const rowDataSx = useMemo(
    () => [columnsStyles.wrapper, classes.rowData],
    [columnsStyles.wrapper, classes.rowData],
  );

  const initialValues = useMemo(
    () =>
      loanDenialReasons.map(({ denialReason, selectedBorrowers }) => ({
        denialReason,
        selectedBorrowers: denialReason.isDenialDueToCredit
          ? borrowerOptions.map(({ value }) => selectedBorrowers.includes(value))
          : [],
      })),
    [loanDenialReasons, borrowerOptions],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateForm,
  } = useForm(initialValues);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const filteredDenialReasonOptions = useMemo(
    () =>
      denialReasonOptions.filter(
        ({ value }) => !form.some(({ denialReason }) => denialReason.id === value),
      ),
    [form, denialReasonOptions],
  );

  const isFormEmpty = !form.filter(({ isDeleted }) => !isDeleted).length;

  const onAddDenial = useCallback(
    (__, { value }) => {
      const denialReason = allDenialReasonsData[value];

      setInForm(`[${form.length}]`, {
        denialReason,
        selectedBorrowers: denialReason.isDenialDueToCredit
          ? borrowerOptions.map(() => borrowerOptions.length === 1)
          : [],
      });

      setIsAddingMode(false);
    },
    [allDenialReasonsData, setInForm, form.length, borrowerOptions],
  );

  const onDeleteDenial = useCallback(
    e => {
      const { index } = e.currentTarget.dataset;

      setInForm(`[${index}]`, {
        ...form[index],
        isDeleted: true,
        selectedBorrowers: [],
      });
    },
    [form, setInForm],
  );

  const onSubmitLoanDenialReasons = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      const updatedLoanDenialReasons = form
        .filter(({ isDeleted }) => !isDeleted)
        .map(({ denialReason, selectedBorrowers }) => ({
          denialReasonId: denialReason.id,
          selectedBorrowers: selectedBorrowers
            .map((value, index) => value && borrowerOptions[index].value)
            .filter(Boolean),
        }));

      dispatch(updateLoanDenialReasonsAction({ loanId, body: updatedLoanDenialReasons }))
        .then(() => {
          dispatch(
            showMessageAction({ message: 'Loan Denial Reasons was updated', variant: 'success' }),
          );
          onExitEditMode();
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [borrowerOptions, dispatch, form, loanId, onExitEditMode, setIsLoading, validateForm],
  );

  return (
    <div className="w-full">
      <Box sx={formContainerSx}>
        <form
          noValidate
          className="pt-48"
          data-ischanged={isFormChanged}
          onSubmit={onSubmitLoanDenialReasons}
        >
          <Box sx={formClasses.buttonsWrapper}>
            <Button
              color="primary"
              data-testid={UnderwritingTestIds.LOAN_DENIAL_FORM_SUBMIT_BUTTON}
              disabled={!isFormChanged || isLoading}
              sx={formClasses.submitButton}
              type="submit"
              variant="contained"
            >
              Done
            </Button>

            <Button
              data-testid={UnderwritingTestIds.LOAN_DENIAL_FORM_CANCEL_BUTTON}
              disabled={isLoading}
              sx={formClasses.cancelButton}
              type="button"
              variant="contained"
              onClick={onExitEditMode}
            >
              Cancel
            </Button>
          </Box>

          {!isFormEmpty && (
            <Box sx={topHeaderSx}>
              <Typography variant="regular14">Denial Reasons</Typography>
              <Typography variant="regular14">HMDA Code</Typography>
              <Typography sx={classes.cellWithCheckbox} variant="regular14">
                Credit Denial
              </Typography>
              {borrowerOptions.map(({ name, value }) => (
                <Typography key={value} sx={classes.cellWithCheckbox} variant="regular14">
                  {name}
                </Typography>
              ))}
              <Typography className="mr-52" sx={classes.cellWithCheckbox} variant="regular14">
                Affiliate credit
              </Typography>
            </Box>
          )}

          {form.map(
            (
              { denialReason: { id, name, HMDACode, isDenialDueToCredit, isAffiliate }, isDeleted },
              reasonIndex,
            ) => {
              if (isDeleted) {
                return null;
              }

              const borrowersError = validationErrors?.[reasonIndex]?.selectedBorrowers;
              const borrowersErrorText = _.isString(borrowersError) ? borrowersError : null;

              return (
                <Box key={id} sx={classes.row}>
                  <Box sx={sideHeaderSx}>
                    <Typography variant="regular14">Denial Reasons</Typography>
                    <Typography variant="regular14">HDMA Code</Typography>
                    <Typography variant="regular14">Credit Denial</Typography>
                    {borrowerOptions.map(({ name, value }) => (
                      <Typography key={value} variant="regular14">
                        {name}
                      </Typography>
                    ))}
                    <Typography variant="regular14">Affiliate credit</Typography>
                  </Box>

                  <Box data-testid={UnderwritingTestIds.getLoanDenialRowId(id)} sx={rowDataSx}>
                    <div className="cursor-default overflow-hidden">
                      <Tooltip title={name}>
                        <div className="truncate">{name}</div>
                      </Tooltip>
                    </div>
                    <div className="cursor-default overflow-hidden">
                      <Tooltip title={HMDACodeMap[HMDACode]}>
                        <div className="truncate">{HMDACodeMap[HMDACode]}</div>
                      </Tooltip>
                    </div>
                    <Box sx={classes.cellWithCheckbox}>
                      <CheckboxCDS
                        disabled
                        dataTestId={UnderwritingTestIds.getLoanDenialCreditCheckboxId(id)}
                        value={isDenialDueToCredit}
                      />
                    </Box>
                    {borrowerOptions.map((value, i) => (
                      <Box key={i} sx={classes.cellWithCheckbox}>
                        <CheckboxCDS
                          withStaticHelper
                          dataTestId={UnderwritingTestIds.getLoanDenialBorrowerCheckboxId(id, i)}
                          disabled={
                            !isDenialDueToCredit ||
                            borrowerOptions.length === 1 ||
                            isDisabledByLock ||
                            lockedFieldsMap.borrowerSelection
                          }
                          error={!!borrowersErrorText}
                          helperText={i === 0 ? borrowersErrorText : ''}
                          name={`[${reasonIndex}].selectedBorrowers[${i}]`}
                          sx={classes.checkbox}
                          value={form[reasonIndex].selectedBorrowers[i]}
                          onChange={handleChange}
                        />
                      </Box>
                    ))}
                    <Box sx={classes.cellWithCheckbox}>
                      <CheckboxCDS
                        disabled
                        dataTestId={UnderwritingTestIds.getLoanDenialAffiliateCheckboxId(id)}
                        value={isAffiliate}
                      />
                    </Box>
                    <IconButton
                      className={clsx('deleteAction', 'w-52')}
                      data-index={reasonIndex}
                      data-testid={UnderwritingTestIds.getLoanDenialDeleteButtonId(id)}
                      disabled={isLoading}
                      onClick={onDeleteDenial}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Box sx={classes.mobileRowActions}>
                    <Button
                      data-index={reasonIndex}
                      data-testid={UnderwritingTestIds.getLoanDenialMobileDeleteButtonId(id)}
                      disabled={isLoading}
                      onClick={onDeleteDenial}
                    >
                      <DeleteIcon />
                      Delete
                    </Button>
                  </Box>
                </Box>
              );
            },
          )}
        </form>
      </Box>

      {!!filteredDenialReasonOptions.length &&
        (isAddingMode ? (
          <AutocompleteCDS
            className="max-w-sm"
            dataTestIdInput={UnderwritingTestIds.LOAN_DENIAL_FORM_NEW_REASON_SELECT}
            disabled={isDisabledByLock || lockedFieldsMap.newDenialReason}
            label="Select Denial Reason"
            name="newDenialReason"
            optionName="name"
            options={filteredDenialReasonOptions}
            value={null}
            onChange={onAddDenial}
          />
        ) : (
          <Button
            className="whitespace-nowrap my-20"
            data-testid={UnderwritingTestIds.LOAN_DENIAL_FORM_ADD_REASON_BUTTON}
            sx={formClasses.addNewButton}
            type="button"
            variant="contained"
            onClick={onShowAddField}
          >
            Add Reason
          </Button>
        ))}
    </div>
  );
};

LoanDenialForm.propTypes = {
  ...WithLoadingPropTypes,
  onExitEditMode: PropTypes.func.isRequired,
};

export default compose(withLoading, memo)(LoanDenialForm);
